import React from "react";
import { styled, TextField, TextFieldProps } from "@mui/material";
import FormControl, { FormControlProps } from "@mui/material/FormControl";
import { inputHeight } from "../theme";

interface StyleProps {
    multilinestyles?: React.CSSProperties;
    inputstyles?: React.CSSProperties;
    labelstyles?: React.CSSProperties;
}

const StyledTextField = styled(TextField, {
    shouldForwardProp: (prop) =>
        prop !== 'multilinestyles' && prop !== 'inputstyles' && prop !== 'labelstyles',
})<StyleProps>(({ multilinestyles, inputstyles, labelstyles }) => ({
    flex: 1,
    width: "100%",
    "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
        transform: "none"
    },
    "& .MuiInputBase-multiline": {
        padding: "12px 0 12px 0",
        height: "152px",
        minHeight: "152px",
        maxHeight: "152px",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "flex-start",

        "& .MuiInputBase-inputMultiline": {
            height: "100% !important",
            maxHeight: "calc(152px - 24px)",
            overflow: "auto !important",
            padding: "0 8px 0 18px"
        },

        ...multilinestyles,
    },
    "& .MuiOutlinedInput-input": {
        padding: "0 8px 0 18px",
        height: inputHeight,

        ...inputstyles
    },
    "& .MuiInputLabel-formControl": {
        position: "inherit"
    },
    "& .MuiOutlinedInput-notchedOutline": {
        borderRadius: "6px",
    },
    "& .MuiOutlinedInput-root": {
        "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderColor: "#f44336",
            borderWidth: "2px"
        }
    },
    "& label": {
        fontSize: "16px",
        alignSelf: "flex-start",
        marginBottom: "4px",
        zIndex: 0,

        ...labelstyles
    },
    "& .MuiInputLabel-asterisk": {
        color: "#f44336"
    },
    "& legend": {
        display: 'none',
        fontSize: "20px",
    },
    '& fieldset': { top: 0 },
}));

export type EnhancedTextFieldProps = Omit<TextFieldProps, 'error'> & StyleProps & {
    error?: string;
    numeric?: boolean;
};

interface FormHelperProps {
    input: {
        name: string;
        value: unknown;
        onChange: React.ChangeEventHandler;
        [key: string]: unknown;
    };
    meta: {
        submitError?: string;
        dirtySinceLastSubmit?: boolean;
        error?: string;
        touched?: boolean;
    };
    label?: string;
    formControlProps?: FormControlProps;
    shrink?: boolean;
    [key: string]: unknown;
}

export const FormHelperEnhancedTextWrapper = ({
    input: { name, value, onChange, ...restInput },
    meta,
    label,
    formControlProps,
    shrink,
    ...rest
}: FormHelperProps) => {
    const showError = !(!(((meta.submitError && !meta.dirtySinceLastSubmit) || meta.error) && meta.touched));

    return (
        <FormControl {...formControlProps} error={showError}>
            <EnhancedTextField
                {...rest}
                {...restInput}
                InputProps={{ inputProps: { min: (rest as any).min, max: (rest as any).max } }}
                value={value as string}
                onChange={onChange}
                label={label}
                error={showError ? meta.error || meta.submitError : ""}
            />
        </FormControl>
    );
};

function EnhancedTextField(props: EnhancedTextFieldProps) {
    const style = props.style ?? {};
    const hasError = props.error !== undefined && props.error !== null && props.error !== "";
    const helperText = props.error ? props.error : props.helperText;

    return (
        <StyledTextField
            {...props}
            error={hasError}
            style={style}
            inputstyles={props.inputstyles}
            multilinestyles={props.multilinestyles}
            labelstyles={props.labelstyles}
            label={props.label}
            required={props.required}
            helperText={helperText}
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            multiline={props.multiline}
            maxRows={props.maxRows}
            value={props.value ?? ""}
            onChange={(e) => {
                if (props.onChange) {
                    e.persist?.();
                    props.onChange(e);
                }
            }}
        />
    );
}

export default EnhancedTextField;
